import store from '../config/configureStore';
import { excludeIncidentFromIBRSList } from 'utils/functions';
import { getService } from './service';
import { notify } from './NotifierReducer';

import {
  formatSaveDateFrom,
  formatSaveDateTo,
  formatSaveDate,
  getCurrentDate,
  getTimePeriod,
} from './TimeReducer';
import { clearScrollPosition } from './ui/UiScrollReducer';
import ServiceName from 'features/shared/constants/serviceName';

export const SET_IBR_EXPAND_OR_NOT_VALUE = 'SET_IBR_EXPAND_OR_NOT_VALUE';
export const SET_BEGINNING_FROM_DATE = 'SET_BEGINNING_FROM_DATE';
export const SET_ENDING_TO_DATE = 'SET_ENDING_TO_DATE';
export const SET_REPORT_DATE = 'SET_REPORT_DATE';
export const SET_BEGINNING_TO_ENDING_TIME_PERIOD = 'SET_BEGINNING_TO_ENDING_TIME_PERIOD';
export const SET_IBR_STATUS_VALUE = 'SET_IBR_STATUS_VALUE';
export const SET_REPORT_STATUS_VALUE = 'SET_REPORT_STATUS_VALUE';
export const SET_APPROVAL_STATUS_VALUE = 'SET_APPROVAL_STATUS_VALUE';
export const SET_IBR_QUICK_FILTER_VALUE = 'SET_IBR_QUICK_FILTER_VALUE';
export const SET_IBR_GROUP_BY_VALUE = 'SET_IBR_GROUP_BY_VALUE';
export const SET_IBR_SORT_BY_VALUE = 'SET_IBR_SORT_BY_VALUE';
export const SET_IBRS_LIST_CURRENT_VIEW = 'SET_IBRS_LIST_CURRENT_VIEW';
export const SET_IBR_SORT_BY_DIRECTION = 'SET_IBR_SORT_BY_DIRECTION';
export const SET_TOTAL_IBR_ACTIVE_INCIDENTS = 'SET_TOTAL_IBR_ACTIVE_INCIDENTS';
export const SET_IBR_ACTIVE_INCIDENTS = 'SET_IBR_ACTIVE_INCIDENTS';
export const SET_IBR_FILTER_FORM_VALUES = 'SET_IBR_FILTER_FORM_VALUES';
export const SET_IS_VIEW_ERROR = 'SET_IS_VIEW_ERROR';
export const SET_VIEW_SUB_STATUS = 'SET_VIEW_SUB_STATUS';
export const SET_IBRS_COUNTS = 'SET_IBRS_COUNTS';
export const CLEAR_ALL_IBRS_FILTERS = 'CLEAR_ALL_IBRS_FILTERS';
export const APPLY_IBRS_FILTERS = 'APPLY_IBRS_FILTERS';
export const RESET_IBRS_FILTERS = 'RESET_IBRS_FILTERS';
export const SET_IBRS_FETCHING = 'SET_IBRS_FETCHING';
export const SET_IBRS_PAGINATION = 'SET_IBRS_PAGINATION';
export const SET_IBRS_PENDING_PRIOR_COUNT = 'SET_IBRS_PENDING_PRIOR_COUNT';

const DEFAULT_IBRS_FILTER_FORM = {
  placeOrAddress: '',
  lowBlock: '',
  highBlock: '',
  predirectional: '',
  streetName: '',
  postdirectional: '',
  streetType: '',
  unitType: '',
  unitNumber: '',
  city: '',
  zone: '',
  partyType: '',
  partyName: null,
  lastName: '',
  firstName: '',
  middleName: '',
  contactMethod: '',
  contactInfo: '',
  cadEventNumber: '',
  status: '',
  tagType: '',
  tagValue: '',
  offense: '',
  disposition: '',
};

const USER_EDITABLE_IBRS_FILTER_KEYS = [
  'beginningFrom',
  'endingTo',
  'beginningToEndingTimePeriod',
  'ibrStatus',
  'reportStatus',
  'quickFilter',
  'groupBy',
  'sortBy',
  'sortByDirection',
  'isViewError',
  'viewSubStatus',
  'filterForm',
];

export const selectIbrsFilterIsDirty = (state) => {
  const draft = state.ibrs?.ibrsFilter;
  const applied = state.ibrs?.appliedIbrsFilter;
  if (!draft || !applied) return false;
  for (const key of USER_EDITABLE_IBRS_FILTER_KEYS) {
    if (key === 'filterForm') {
      if (JSON.stringify(draft.filterForm || {}) !== JSON.stringify(applied.filterForm || {})) {
        return true;
      }
    } else if (key === 'beginningFrom' || key === 'endingTo') {
      const a = draft[key] ? new Date(draft[key]).getTime() : null;
      const b = applied[key] ? new Date(applied[key]).getTime() : null;
      if (a !== b) return true;
    } else if (draft[key] !== applied[key]) {
      return true;
    }
  }
  return false;
};

const buildDefaultIbrsDateRange = () => {
  const { fromDate, toDate } = getTimePeriod('Last Month');
  return {
    beginningFrom: fromDate,
    endingTo: toDate,
    beginningToEndingTimePeriod: 'Last Month',
  };
};

export const changeIBRSSubmittedState = async (incidentID, status, mulipleIncidents = false) => {
  const client = store.store.getState().websocket.websocket;
  const lastFromLocation = store.store.getState().incident.locationFrom;
  const service = client.service('rms-create-report');
  service.timeout = 900000;

  let dataToSave = {
    ibrIsSubmitted: status,
    ibrLastChange: formatSaveDate(getCurrentDate()),
    mulipleIncidents,
  };

  if (lastFromLocation !== 'ibrs') {
    await service.patch(incidentID, dataToSave);
    updateNibrsSubmissionStatus(incidentID, dataToSave.ibrLastChange);
  }
};

/**
 * Update incident current submission status based on ibrLastChange.
 *
 * @param {number} incidentId - ptsIncidentID - Incident id
 * @param {date} ibrLastChange - latest ibrs property updated date
 * @returns {void}
 */
export const updateNibrsSubmissionStatus = async (incidentId, ibrLastChange) => {
  const client = store.store.getState().websocket.websocket;
  const userName = store.store.getState().user.userData?.user?.Username;
  const nibrsService = client.service('rms-nibrs');
  nibrsService.timeout = 900000;

  let incNibrsStatuses = await nibrsService.find({
    query: {
      ptsIncidentID: incidentId,
    },
  });

  if (incNibrsStatuses.length === 0) {
    // Update status to "Queued for Submission" when any status is not available for the incident
    upsertRmsNibrsRecord(incidentId, ibrLastChange, 'Queued for Submission', userName, 'create');
  } else {
    if (incNibrsStatuses[0].Type !== 'Queued for Submission') {
      // Update status to "Queued for Submission" when last nibrs status is other than "Queued for Submission"
      upsertRmsNibrsRecord(incidentId, ibrLastChange, 'Queued for Submission', userName, 'create');
    } else {
      let ibrLastChangeMonth = new Date(ibrLastChange).getMonth();
      let ibrLastChangeYear = new Date(ibrLastChange).getFullYear();
      let reportDateMonth = new Date(incNibrsStatuses[0].ReportDate).getMonth();
      let reportDateYear = new Date(incNibrsStatuses[0].ReportDate).getFullYear();

      // Update status to "Queued for Submission" when last "Queued for Submission" status update was done month/year before
      if (ibrLastChangeMonth !== reportDateMonth || ibrLastChangeYear !== reportDateYear) {
        upsertRmsNibrsRecord(
          incidentId,
          ibrLastChange,
          'Queued for Submission',
          userName,
          'create'
        );
      } else {
        // Update status to "Queued for Submission" staus ReportDate in sync with ibrLastChange
        upsertRmsNibrsRecord(
          incNibrsStatuses[0].ROWGUID,
          ibrLastChange,
          'Queued for Submission',
          userName,
          'patch'
        );
      }
    }
  }
};

/**
 * Create/update nibrs table for incident.
 *
 * @param {number} id - ptsIncidentID/ROWGUID
 * @param {date} ibrLastChange - latest ibrs property updated date
 * @param {number} type - incident submission status
 * @param {number} username - CreatedBy/UpdatedBy
 * @param {number} method - create/patch
 * @returns {void}
 */
export const upsertRmsNibrsRecord = async (id, ibrLastChange, type, username, method) => {
  const client = store.store.getState().websocket.websocket;
  const nibrsService = client.service('rms-nibrs');
  nibrsService.timeout = 900000;

  if (method === 'create') {
    await nibrsService.create({
      ptsIncidentID: id,
      ReportDate: ibrLastChange,
      Type: type,
      createdBy: username,
    });
  } else if (method === 'patch') {
    await nibrsService.patch(id, {
      ReportDate: ibrLastChange,
      Type: type,
      UpdatedBy: username,
    });
  }
};

export const getAllActiveIncidents = (user, page, rowsPerPage) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (state.ibrs.isFetchingIbrs) return;

    const client = state.websocket.websocket;
    if (!client) return;

    const incidentFilter = state.ibrs.appliedIbrsFilter || state.ibrs.ibrsFilter;
    const agencyID = state.user.userAgency;
    const userCurrentClearanceLevel = state.user.userCurrentClearanceLevel;

    dispatch({ type: SET_IBRS_FETCHING, payload: true });
    try {
      const service =
        incidentFilter.ibrsListCurrentView === 'ibrsCardView'
          ? client.service('rms-ibrs-incident')
          : client.service('rms-ibrs-submitted-incidents');
      service.timeout = 50000;

      const result = await service.find({
        query: {
          page,
          rowsPerPage: rowsPerPage,
          clearanceLevel: parseInt(userCurrentClearanceLevel.Level) || 0,

          beginningFrom: incidentFilter?.beginningFrom
            ? formatSaveDateFrom(incidentFilter?.beginningFrom)
            : null,
          endingTo: incidentFilter?.endingTo ? formatSaveDateTo(incidentFilter?.endingTo) : null,
          quickFilter: incidentFilter?.quickFilter || null,
          reportStatus: incidentFilter?.reportStatus || null,
          ibrStatus: incidentFilter?.ibrStatus || null,
          sortBy: incidentFilter?.sortBy,
          isViewError:
            incidentFilter?.isViewError === 'true'
              ? true
              : incidentFilter?.isViewError === 'false'
              ? false
              : null,
          viewSubStatus:
            incidentFilter?.ibrsListCurrentView === 'ibrsSubmittedIncidentView' ||
            incidentFilter?.viewSubStatus === 'all'
              ? null
              : incidentFilter?.viewSubStatus ?? null,
          sortByDirection: incidentFilter?.sortByDirection,
          partyName: incidentFilter?.filterForm?.partyName?.FullName || null,
          ptsPersonID: incidentFilter?.filterForm?.partyName?.ptsPersonID || null,
          partyType: incidentFilter?.filterForm?.partyType || null,
          ptsCityID: incidentFilter?.filterForm?.ptsCityID || null,
          ptsAddressID: incidentFilter?.filterForm?.ptsAddressID || null,
          streetName: incidentFilter?.filterForm?.streetName || null,
          streetType: incidentFilter?.filterForm?.streetType || null,
          predirectional: incidentFilter?.filterForm?.predirectional || null,
          postdirectional: incidentFilter?.filterForm?.postdirectional || null,
          unitType: incidentFilter?.filterForm?.unitType || null,
          unitNumber: incidentFilter?.filterForm?.unitNumber || null,
          lastName: incidentFilter?.filterForm?.lastName || null,
          firstName: incidentFilter?.filterForm?.firstName || null,
          middleName: incidentFilter?.filterForm?.middleName || null,
          zone: incidentFilter?.filterForm?.zone || null,
          cadEventNumber: incidentFilter?.filterForm?.cadEventNumber || null,
          status: incidentFilter?.filterForm?.status || null,
          tagType: incidentFilter?.filterForm?.tagType || null,
          tagValue: incidentFilter?.filterForm?.tagValue || null,
          offense: incidentFilter?.filterForm?.offense || null,
          disposition: incidentFilter?.filterForm?.disposition || null,
          lowBlock: incidentFilter?.filterForm?.lowBlock || null,
          highBlock: incidentFilter?.filterForm?.highBlock || null,
          contactMethod: incidentFilter?.filterForm?.contactMethod || null,
          contactInfo: incidentFilter?.filterForm?.contactInfo || null,
          agencyId: agencyID,
          ibrsVersion: user?.ibrsVersion,
        },
      });

      let activeList = [];

      dispatch({
        type: SET_TOTAL_IBR_ACTIVE_INCIDENTS,
        payload: result?.length ? result[0].Count : 0,
      });

      dispatch({
        type: SET_IBRS_COUNTS,
        payload: {
          all: result?.[0]?.AllCount ?? 0,
          error: result?.[0]?.ErrorCount ?? 0,
          noError: result?.[0]?.NoErrorCount ?? 0,
          allStatus: result?.[0]?.AllStatusIncidentCount ?? 0,
          new: result?.[0]?.NewIncidentCount ?? 0,
          resubmitted: result?.[0]?.ResubmittedIncidentCount ?? 0,
        },
      });

      result?.map((r) => {
        /* Check for excluding incident for 0 FBICode & exclude offenses */
        let incidentOffenses = r.Offenses ? JSON.parse(r.Offenses) : [];
        if (
          incidentOffenses &&
          incidentOffenses.length > 0 &&
          excludeIncidentFromIBRSList(incidentOffenses) === true
        )
          return;

        activeList.push({
          agencyID: r.AgencyID,
          callType: r.CallType,
          completed: r.Completed,
          cancelled: r.Cancelled,
          dispatched: r.Dispatched,
          createdBy: r.CreatedBy,
          occurred: r.Occurred,
          updated: r.Updated,
          updatedBy: r.UpdatedBy,
          received: r.Received,
          enroute: r.Enroute,
          hasNibrsSubmission: r.HasSubmission,
          hasAnyHistory: r.HasAnyHistory,
          primaryAddress: r.FullAddress,
          eventType: r.CallType,
          incidentId: r.ptsIncidentID,
          CustomId: r.IncidentID,
          ibrLastChange: r.ibrLastChange,
          ptsIncidentId: r.ptsIncidentID,
          IsJuvenileReport: r.IsJuvenileReport,
          parties: r.Parties ? JSON.parse(r.Parties) : [],
          properties: r.Properties ? JSON.parse(r.Properties) : [],
          offenses: r.Offenses ? JSON.parse(r.Offenses) : [],
          statuses: r.Statuses ? JSON.parse(r.Statuses) : [],
          synopsis: r.Synopsis ? r.Synopsis : '',
          tags: r.Tags ? JSON.parse(r.Tags) : [],
          nibrsErrors: r.IBRS ? JSON.parse(r.IBRS) : [],
        });
      });

      dispatch({ type: SET_IBR_ACTIVE_INCIDENTS, payload: activeList });
      dispatch(setIbrsIncidents(activeList));
    } catch (error) {
      if (error.code === 401) {
        console.log('Unauthenticated');
      } else {
        console.log(error);
      }
    } finally {
      dispatch({ type: SET_IBRS_FETCHING, payload: false });
    }
  };
};

export const fetchPendingPriorIncidentCount = () => async (dispatch, getState) => {
  const state = getState();

  const incidentFilter = state.ibrs.appliedIbrsFilter || state.ibrs.ibrsFilter;
  const beginningFrom = incidentFilter?.beginningFrom;

  if (!beginningFrom || incidentFilter?.ibrsListCurrentView !== 'ibrsCardView') {
    dispatch({ type: SET_IBRS_PENDING_PRIOR_COUNT, payload: 0 });
    return;
  }

  const agencyID = state.user.userAgency;
  const userCurrentClearanceLevel = state.user.userCurrentClearanceLevel;

  try {
    const service = getService(ServiceName.rms_ibrs_incident);

    const endingToBefore = new Date(beginningFrom);
    endingToBefore.setDate(endingToBefore.getDate() - 1);

    const result = await service.find({
      query: {
        page: 1,
        rowsPerPage: 1,
        clearanceLevel: parseInt(userCurrentClearanceLevel.Level) || 0,
        beginningFrom: null,
        endingTo: formatSaveDateTo(endingToBefore),
        viewSubStatus: 'all',
        quickFilter: incidentFilter?.quickFilter || null,
        reportStatus: incidentFilter?.reportStatus || null,
        ibrStatus: incidentFilter?.ibrStatus || null,
        isViewError:
          incidentFilter?.isViewError === 'true'
            ? true
            : incidentFilter?.isViewError === 'false'
            ? false
            : null,
        partyName: incidentFilter?.filterForm?.partyName?.FullName || null,
        ptsPersonID: incidentFilter?.filterForm?.partyName?.ptsPersonID || null,
        partyType: incidentFilter?.filterForm?.partyType || null,
        ptsAddressID: incidentFilter?.filterForm?.ptsAddressID || null,
        streetName: incidentFilter?.filterForm?.streetName || null,
        streetType: incidentFilter?.filterForm?.streetType || null,
        predirectional: incidentFilter?.filterForm?.predirectional || null,
        postdirectional: incidentFilter?.filterForm?.postdirectional || null,
        unitType: incidentFilter?.filterForm?.unitType || null,
        unitNumber: incidentFilter?.filterForm?.unitNumber || null,
        lastName: incidentFilter?.filterForm?.lastName || null,
        firstName: incidentFilter?.filterForm?.firstName || null,
        middleName: incidentFilter?.filterForm?.middleName || null,
        zone: incidentFilter?.filterForm?.zone || null,
        cadEventNumber: incidentFilter?.filterForm?.cadEventNumber || null,
        tagType: incidentFilter?.filterForm?.tagType || null,
        tagValue: incidentFilter?.filterForm?.tagValue || null,
        offense: incidentFilter?.filterForm?.offense || null,
        disposition: incidentFilter?.filterForm?.disposition || null,
        lowBlock: incidentFilter?.filterForm?.lowBlock || null,
        highBlock: incidentFilter?.filterForm?.highBlock || null,
        contactMethod: incidentFilter?.filterForm?.contactMethod || null,
        contactInfo: incidentFilter?.filterForm?.contactInfo || null,
        sortBy: null,
        sortByDirection: 'DESC',
        agencyId: agencyID,
        countOnly: true,
      },
    });

    const count = result?.[0]?.AllStatusIncidentCount ?? 0;
    dispatch({ type: SET_IBRS_PENDING_PRIOR_COUNT, payload: count });
  } catch (error) {
    dispatch({ type: SET_IBRS_PENDING_PRIOR_COUNT, payload: 0 });
  }
};

export const getAllActiveIncidentsForSelectALL = async (user, page, rowsPerPage) => {
  const client = store.store.getState().websocket.websocket;
  const incidentFilter = store.store.getState().ibrs.ibrsFilter;
  const agencyID = store.store.getState().user.userAgency;
  const codeIBRStatus = store.store.getState().dictionary.codeIBRStatus;
  const userCurrentClearanceLevel = store.store.getState().user.userCurrentClearanceLevel;

  try {
    const service =
      incidentFilter.ibrsListCurrentView === 'ibrsCardView'
        ? client.service('rms-ibrs-incident')
        : client.service('rms-ibrs-submitted-incidents');
    service.timeout = 50000;

    const result = await service.find({
      query: {
        page,
        rowsPerPage: rowsPerPage,
        clearanceLevel: parseInt(userCurrentClearanceLevel.Level) || 0,

        beginningFrom: incidentFilter?.beginningFrom
          ? formatSaveDateFrom(incidentFilter?.beginningFrom)
          : null,
        endingTo: incidentFilter?.endingTo ? formatSaveDateTo(incidentFilter?.endingTo) : null,
        quickFilter: incidentFilter?.quickFilter || null,
        reportStatus: incidentFilter?.reportStatus || null,
        ibrStatus: incidentFilter?.ibrStatus || null,
        sortBy: incidentFilter?.sortBy,
        isViewError:
          incidentFilter?.isViewError === 'true'
            ? true
            : incidentFilter?.isViewError === 'false'
            ? false
            : null,
        viewSubStatus:
          incidentFilter?.ibrsListCurrentView === 'ibrsSubmittedIncidentView' ||
          incidentFilter?.viewSubStatus === 'all'
            ? null
            : incidentFilter?.viewSubStatus ?? null,
        sortByDirection: incidentFilter?.sortByDirection,
        partyName: incidentFilter?.filterForm?.partyName?.FullName || null,
        ptsPersonID: incidentFilter?.filterForm?.partyName?.ptsPersonID || null,
        partyType: incidentFilter?.filterForm?.partyType || null,
        ptsCityID: incidentFilter?.filterForm?.ptsCityID || null,
        ptsAddressID: incidentFilter?.filterForm?.ptsAddressID || null,
        streetName: incidentFilter?.filterForm?.streetName || null,
        streetType: incidentFilter?.filterForm?.streetType || null,
        predirectional: incidentFilter?.filterForm?.predirectional || null,
        postdirectional: incidentFilter?.filterForm?.postdirectional || null,
        unitType: incidentFilter?.filterForm?.unitType || null,
        unitNumber: incidentFilter?.filterForm?.unitNumber || null,
        lastName: incidentFilter?.filterForm?.lastName || null,
        firstName: incidentFilter?.filterForm?.firstName || null,
        middleName: incidentFilter?.filterForm?.middleName || null,
        zone: incidentFilter?.filterForm?.zone || null,
        cadEventNumber: incidentFilter?.filterForm?.cadEventNumber || null,
        status: incidentFilter?.filterForm?.status || null,
        tagType: incidentFilter?.filterForm?.tagType || null,
        tagValue: incidentFilter?.filterForm?.tagValue || null,
        offense: incidentFilter?.filterForm?.offense || null,
        disposition: incidentFilter?.filterForm?.disposition || null,
        lowBlock: incidentFilter?.filterForm?.lowBlock || null,
        highBlock: incidentFilter?.filterForm?.highBlock || null,
        contactMethod: incidentFilter?.filterForm?.contactMethod || null,
        contactInfo: incidentFilter?.filterForm?.contactInfo || null,
        agencyId: agencyID,
      },
    });
    let activeList = [];

    result?.map((r) => {
      activeList.push({
        agencyID: r.AgencyID,
        callType: r.CallType,
        completed: r.Completed,
        cancelled: r.Cancelled,
        dispatched: r.Dispatched,
        createdBy: r.CreatedBy,
        occurred: r.Occurred,
        updated: r.Updated,
        updatedBy: r.UpdatedBy,
        received: r.Received,
        enroute: r.Enroute,
        primaryAddress: r.FullAddress,
        eventType: r.CallType,
        incidentId: r.ptsIncidentID,
        CustomId: r.IncidentID,
        ptsIncidentId: r.ptsIncidentID,
        IsJuvenileReport: r.IsJuvenileReport,
        parties: r.Parties ? JSON.parse(r.Parties) : [],
        properties: r.Properties ? JSON.parse(r.Properties) : [],
        offenses: r.Offenses ? JSON.parse(r.Offenses) : [],
        statuses: r.Statuses ? JSON.parse(r.Statuses) : [],
        synopsis: r.Synopsis ? r.Synopsis : '',
        tags: r.Tags ? JSON.parse(r.Tags) : [],
        nibrsErrors: r.IBRS ? JSON.parse(r.IBRS) : [],
        // This call passes page='ALL', which routes the API to
        // getSelectStatement(fetchAll=true) — a branch that deliberately omits
        // every FOR JSON projection, including the `IBRS` blob. So
        // `nibrsErrors` above is ALWAYS [] here and must not be used to decide
        // whether an incident has errors. That branch does return the
        // `HasError` bit, so carry it through: it is the only error signal
        // available for rows fetched this way.
        hasError: !!r.HasError,
      });
    });

    return activeList;
  } catch (error) {
    if (error.code === 401) {
      console.log('Unauthenticated');
    } else {
      console.log(error);
    }
    return [];
  }
};

export const setIbrsIncidents = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_IBR_ACTIVE_INCIDENTS, payload: value });
  };
};
export const getSubmissionHistory = async (ptsIncidentId, opts = {}) => {
  const service = getService('rms-nibrs');
  const response = await service.find({
    query: {
      ptsIncidentID: ptsIncidentId,
      includeRewound: !!opts.includeRewound,
    },
  });
  return response;
};

export const rewindSubmissionHistory = (payload) => async (dispatch) => {
  try {
    const service = getService('rms-ibrs-rewind');
    const result = await service.create(payload);
    dispatch(notify('Rewind complete.', 'success'));
    return result;
  } catch (err) {
    dispatch(notify(err.message || 'Rewind failed.', 'error'));
    throw err;
  }
};

export const setExpandOrNotValue = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_IBR_EXPAND_OR_NOT_VALUE, payload: value });
  };
};

export const setBeginningFromDate = (date) => {
  return (dispatch) => {
    dispatch({ type: SET_BEGINNING_FROM_DATE, payload: date });
  };
};

export const setEndingToDate = (date) => {
  return (dispatch) => {
    dispatch({ type: SET_ENDING_TO_DATE, payload: date });
  };
};

export const setReportDate = (date) => {
  return (dispatch) => {
    dispatch({ type: SET_REPORT_DATE, payload: date });
  };
};

export const setBeginningToEndingTimePeriod = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_BEGINNING_TO_ENDING_TIME_PERIOD, payload: value });
  };
};

export const setIbrStatusValue = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_IBR_STATUS_VALUE, payload: value });
  };
};

export const setReportStatusValue = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_REPORT_STATUS_VALUE, payload: value });
  };
};

export const setApprovalStatusValue = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_APPROVAL_STATUS_VALUE, payload: value });
  };
};

export const setQuickFilterValue = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_IBR_QUICK_FILTER_VALUE, payload: value });
  };
};

export const setGroupByValue = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_IBR_GROUP_BY_VALUE, payload: value });
  };
};

export const setSortByValue = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_IBR_SORT_BY_VALUE, payload: value });
  };
};

export const setIbrsListCurrentView = (view) => {
  return (dispatch) => {
    dispatch({ type: SET_IBRS_LIST_CURRENT_VIEW, payload: view });
  };
};

export const setSortByDirection = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_IBR_SORT_BY_DIRECTION, payload: value });
  };
};

export const setIsViewError = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_IS_VIEW_ERROR, payload: value });
  };
};

export const setViewSubStatus = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_VIEW_SUB_STATUS, payload: value });
  };
};

export const setFilterFormValues = (value) => {
  return (dispatch) => {
    dispatch({ type: SET_IBR_FILTER_FORM_VALUES, payload: value });
  };
};

export const clearAllFilters = () => {
  const filterValue = {
    beginningFrom: null,
    endingTo: null,
    beginningToEndingTimePeriod: '',
    ibrStatus: '',
    groupBy: '',
    sortBy: 'Incident Number',
    sortByDirection: 'DESC',
    isViewError: 'all',
    viewSubStatus: 'all',
    reportStatus: '',
    quickFilter: '',
    filterForm: { ...DEFAULT_IBRS_FILTER_FORM },
  };

  return (dispatch) => {
    dispatch({ type: CLEAR_ALL_IBRS_FILTERS, payload: filterValue });
    dispatch({ type: SET_IBRS_PAGINATION, payload: { page: 0, rowsPerPage: 25 } });
  };
};

/**
 * Snapshot the current draft filter (state.ibrs.ibrsFilter) into appliedIbrsFilter,
 * optionally merging overrides first. The data-fetch effect listens on appliedIbrsFilter,
 * so this is what actually triggers a request.
 *
 * Use cases:
 *   - Search button: applyIbrsFilters() -- snapshot draft as-is
 *   - Tab switch:   applyIbrsFilters({ ibrsListCurrentView: newView }) -- update tab + apply atomically
 *   - Mount default: applyIbrsFilters({ beginningFrom, endingTo, beginningToEndingTimePeriod: 'Last Month' })
 */
export const applyIbrsFilters = (overrides) => (dispatch) => {
  dispatch({ type: APPLY_IBRS_FILTERS, payload: overrides || null });
  dispatch({ type: SET_IBRS_PAGINATION, payload: { page: 0, rowsPerPage: 25 } });
  dispatch(clearScrollPosition('IBRSListView'));
};

export const resetIbrsDraftFilters = () => (dispatch) => {
  const defaults = buildDefaultIbrsDateRange();
  dispatch({ type: RESET_IBRS_FILTERS, payload: defaults });
  // Keep reportDate in sync with endingTo -- the Create Report modal seeds from it
  // and the date-picker onChange normally syncs it, but resetting bypasses that path.
  dispatch({ type: SET_REPORT_DATE, payload: defaults.endingTo });
  dispatch({ type: SET_IBRS_PAGINATION, payload: { page: 0, rowsPerPage: 25 } });
  dispatch(clearScrollPosition('IBRSListView'));
};

export const setIbrsPagination = (page, rowsPerPage) => (dispatch) => {
  dispatch({ type: SET_IBRS_PAGINATION, payload: { page, rowsPerPage } });
  dispatch(clearScrollPosition('IBRSListView'));
};


/**
 * On screen mount: if no date range is set, seed defaults to "Last Month",
 * then snapshot to appliedIbrsFilter so the initial fetch is bounded.
 * If a date range is already set (user navigated back, etc.), just snapshot
 * the current draft so the filters take effect.
 */
export const initIbrsDefaultDateRange = () => (dispatch, getState) => {
  const f = getState().ibrs?.ibrsFilter;
  if (!f?.beginningFrom && !f?.endingTo) {
    const defaults = buildDefaultIbrsDateRange();
    dispatch({ type: APPLY_IBRS_FILTERS, payload: defaults });
    dispatch({ type: SET_REPORT_DATE, payload: defaults.endingTo });
  } else {
    dispatch({ type: APPLY_IBRS_FILTERS, payload: null });
    // Defensive: if reportDate is null but a date range exists (e.g. legacy state),
    // sync reportDate to endingTo so the Create Report modal opens with the end date.
    if (!f.reportDate && f.endingTo) {
      dispatch({ type: SET_REPORT_DATE, payload: f.endingTo });
    }
  }
  dispatch(fetchPendingPriorIncidentCount());
};

export default function reducer(
  state = {
    ibrsFilter: {
      expandOrNotValue: false,
      beginningFrom: null,
      endingTo: null,
      reportDate: null,
      beginningToEndingTimePeriod: '',
      ibrStatus: '',
      reportStatus: '',
      approvalStatus: '',
      quickFilter: '',
      groupBy: '',
      sortBy: 'Incident Number',
      ibrsListCurrentView: 'ibrsCardView',
      isViewError: 'all',
      viewSubStatus: 'all',
      sortByDirection: 'DESC',
      ibrsIncidents: [],
      totalIncidents: 0,
      filterForm: { ...DEFAULT_IBRS_FILTER_FORM },
    },
    appliedIbrsFilter: null,
    isFetchingIbrs: false,
    ibrsPagination: { page: 0, rowsPerPage: 25 },
    counts: {
      all: 0,
      error: 0,
      noError: 0,
      allStatus: 0,
      new: 0,
      resubmitted: 0,
    },
    pendingPriorCount: 0,
  },
  action
) {
  switch (action.type) {
    case SET_IBR_EXPAND_OR_NOT_VALUE:
      return {
        ...state,
        ibrsFilter: { ...state.ibrsFilter, expandOrNotValue: action.payload },
      };
    case SET_BEGINNING_FROM_DATE:
      return { ...state, ibrsFilter: { ...state.ibrsFilter, beginningFrom: action.payload } };
    case SET_ENDING_TO_DATE:
      return { ...state, ibrsFilter: { ...state.ibrsFilter, endingTo: action.payload } };
    case SET_REPORT_DATE:
      return { ...state, ibrsFilter: { ...state.ibrsFilter, reportDate: action.payload } };
    case SET_BEGINNING_TO_ENDING_TIME_PERIOD:
      return {
        ...state,
        ibrsFilter: { ...state.ibrsFilter, beginningToEndingTimePeriod: action.payload },
      };
    case SET_IBR_STATUS_VALUE:
      return {
        ...state,
        ibrsFilter: { ...state.ibrsFilter, ibrStatus: action.payload },
      };
    case SET_REPORT_STATUS_VALUE:
      return {
        ...state,
        ibrsFilter: { ...state.ibrsFilter, reportStatus: action.payload },
      };
    case SET_APPROVAL_STATUS_VALUE:
      return {
        ...state,
        ibrsFilter: { ...state.ibrsFilter, approvalStatus: action.payload },
      };
    case SET_IBR_QUICK_FILTER_VALUE:
      return { ...state, ibrsFilter: { ...state.ibrsFilter, quickFilter: action.payload } };
    case SET_IBR_GROUP_BY_VALUE:
      return { ...state, ibrsFilter: { ...state.ibrsFilter, groupBy: action.payload } };
    case SET_IBR_SORT_BY_VALUE:
      return { ...state, ibrsFilter: { ...state.ibrsFilter, sortBy: action.payload } };
    case SET_IBRS_LIST_CURRENT_VIEW:
      return {
        ...state,
        ibrsFilter: { ...state.ibrsFilter, ibrsListCurrentView: action.payload },
      };
    case SET_IBR_SORT_BY_DIRECTION:
      return {
        ...state,
        ibrsFilter: { ...state.ibrsFilter, sortByDirection: action.payload },
      };
    case SET_IS_VIEW_ERROR:
      return {
        ...state,
        ibrsFilter: { ...state.ibrsFilter, isViewError: action.payload },
      };
    case SET_VIEW_SUB_STATUS:
      return {
        ...state,
        ibrsFilter: { ...state.ibrsFilter, viewSubStatus: action.payload },
      };
    case SET_IBRS_COUNTS:
      return {
        ...state,
        counts: action.payload,
      };
    case SET_IBR_ACTIVE_INCIDENTS:
      return {
        ...state,
        ibrsIncidents: action.payload,
      };
    case SET_TOTAL_IBR_ACTIVE_INCIDENTS:
      return {
        ...state,
        totalIncidents: action.payload,
      };
    case SET_IBR_FILTER_FORM_VALUES:
      return {
        ...state,
        ibrsFilter: { ...state.ibrsFilter, filterForm: { ...action.payload } },
      };
    case CLEAR_ALL_IBRS_FILTERS:
      return {
        ...state,
        ibrsFilter: {
          ...state.ibrsFilter,
          beginningFrom: action.payload.beginningFrom,
          endingTo: action.payload.endingTo,
          groupBy: action.payload.groupBy,
          sortBy: action.payload.sortBy,
          sortByDirection: action.payload.sortByDirection,
          isViewError: action.payload.isViewError,
          viewSubStatus: action.payload.viewSubStatus,
          beginningToEndingTimePeriod: action.payload.beginningToEndingTimePeriod,
          ibrStatus: action.payload.ibrStatus,
          reportStatus: action.payload.reportStatus,
          quickFilter: action.payload.quickFilter,
          filterForm: action.payload.filterForm,
        },
      };
    case APPLY_IBRS_FILTERS: {
      const merged = action.payload
        ? { ...state.ibrsFilter, ...action.payload }
        : state.ibrsFilter;
      return {
        ...state,
        ibrsFilter: merged,
        appliedIbrsFilter: merged,
      };
    }
    case RESET_IBRS_FILTERS: {
      const dateDefaults = action.payload || {};
      const reset = {
        ...state.ibrsFilter,
        beginningFrom: null,
        endingTo: null,
        beginningToEndingTimePeriod: '',
        ibrStatus: '',
        reportStatus: '',
        approvalStatus: '',
        quickFilter: '',
        groupBy: '',
        sortBy: 'Incident Number',
        sortByDirection: 'DESC',
        isViewError: 'all',
        viewSubStatus: 'all',
        filterForm: { ...DEFAULT_IBRS_FILTER_FORM },
        ...dateDefaults,
      };
      return {
        ...state,
        ibrsFilter: reset,
        appliedIbrsFilter: reset,
      };
    }
    case SET_IBRS_FETCHING:
      return { ...state, isFetchingIbrs: !!action.payload };
    case SET_IBRS_PENDING_PRIOR_COUNT:
      return { ...state, pendingPriorCount: action.payload };
    case SET_IBRS_PAGINATION: {
      const { page, rowsPerPage } = action.payload;
      return {
        ...state,
        ibrsPagination: {
          ...state.ibrsPagination,
          page,
          rowsPerPage,
        },
      };
    }
  }

  return state;
}
